import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import CodeInput from '@/components/Custom/CodeInput';
import {
  type MemberType,
  MemberTypeSelector,
} from '@/components/People/PeopleFilterSelector';
import Box from '@/components/Unistyles/Box';
import Text from '@/components/Unistyles/Text';
import WelcomeModalStepFrame from '@/components/Welcome/WelcomeModalStepFrame';
import {
  WelcomeActions,
  WelcomeCard,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import { graphql } from '@/gql/graphql';
import useHeaderBackButton from '@/hooks/useHeaderBackButton';
import useAuthStore from '@/stores/useAuthStore';
import useJoinIntentStore, { type JoinKind } from '@/stores/useJoinIntentStore';
import { gotoOrganizationDetail, gotoTeamDetail } from '@/stores/useRootStore';
import isJoinTerminalFailure from '@/utils/isJoinTerminalFailure';
import { resolveJoinMemberType } from '@/utils/joinKind';
import normalizeInviteCode from '@/utils/normalizeInviteCode';
import useInvitePreview from './useInvitePreview';

const AcceptJoin = graphql(`
  mutation AcceptJoin(
    $kind: invite_entry_kind!
    $code: String!
    $memberType: String!
  ) {
    accept_invite_entry(
      args: { kind: $kind, code: $code, requested_member_type: $memberType }
    ) {
      entity_id
      accepted
    }
  }
`);

const GetJoinAccountState = graphql(/* GraphQL */ `
  query GetJoinAccountState($id: String!) {
    person_by_pk(id: $id) {
      id
      is_complete
    }
  }
`);

type FormValues = {
  code: string;
};

const resolver = Yup.object().shape({
  code: Yup.string().required('Invite code is required').length(8),
});

export default function Join({ kind = 'team' }: { kind?: JoinKind }) {
  const { code, memberType } = useLocalSearchParams<{
    code?: string;
    memberType?: string;
  }>();
  const initialCode = normalizeInviteCode(code || '');
  const initialMemberType = resolveJoinMemberType(kind, memberType);

  return (
    <JoinForm
      initialCode={initialCode}
      initialMemberType={initialMemberType}
      kind={kind}
      key={`${kind}:${initialCode}:${initialMemberType}`}
    />
  );
}

function JoinForm({
  initialCode,
  initialMemberType,
  kind,
}: {
  initialCode: string;
  initialMemberType: MemberType;
  kind: JoinKind;
}) {
  const router = useRouter();
  const authStatus = useAuthStore((state) => state.status);
  const uid = useAuthStore((state) => state.uid);
  const clearJoinIntent = useJoinIntentStore((state) => state.clearJoinIntent);
  const discardJoinIntent = useJoinIntentStore(
    (state) => state.discardJoinIntent,
  );
  const markJoinIntentForResume = useJoinIntentStore(
    (state) => state.markJoinIntentForResume,
  );
  const setJoinIntent = useJoinIntentStore((state) => state.setJoinIntent);
  const [, acceptJoin] = useMutation(AcceptJoin);
  const [
    { data: accountData, error: accountError, fetching: accountFetching },
  ] = useQuery({
    query: GetJoinAccountState,
    variables: { id: uid || '' },
    pause: authStatus !== 'in' || !uid,
    requestPolicy: 'network-only',
  });
  const dismissJoin = () => {
    clearJoinIntent();
    router.back();
  };
  const headerLeft = useHeaderBackButton(dismissJoin);

  const form = useForm<FormValues>({
    resolver: yupResolver(resolver),
    defaultValues: {
      code: initialCode,
    },
    mode: 'onChange',
  });

  const {
    control,
    clearErrors,
    setError,
    formState: { isValid },
  } = form;

  const enteredCode = useWatch({ control, name: 'code' });
  const normalizedCode = normalizeInviteCode(enteredCode);
  const hasCompleteCode = normalizedCode.length === 8;
  const {
    preview,
    error: previewError,
    fetching: previewFetching,
    received: previewReceived,
    retry: retryPreview,
  } = useInvitePreview(kind, normalizedCode);
  const [memberType, setMemberType] = useState(initialMemberType);
  const joinUnavailable =
    hasCompleteCode &&
    !previewFetching &&
    !previewError &&
    previewReceived &&
    !preview;
  const [isAccepting, setIsAccepting] = useState(false);

  const isAccountComplete = accountData?.person_by_pk?.is_complete === true;
  const needsProfileCompletion =
    authStatus === 'in' && accountData?.person_by_pk?.is_complete === false;
  const isBusy = previewFetching || isAccepting || accountFetching;

  const actionLabel = preview
    ? needsProfileCompletion
      ? 'Continue'
      : isAccepting
        ? 'Joining...'
        : `Join ${kind === 'organization' ? 'organization' : 'team'}`
    : previewFetching
      ? 'Checking...'
      : previewError
        ? 'Try again'
        : `Join ${kind === 'organization' ? 'organization' : 'team'}`;

  useEffect(() => {
    if (!joinUnavailable) return;
    discardJoinIntent({
      kind,
      code: normalizedCode,
      memberType,
    });
  }, [discardJoinIntent, joinUnavailable, kind, memberType, normalizedCode]);

  const continueToAcceptance = async () => {
    if (!preview || isAccepting) return;

    const code = normalizeInviteCode(form.getValues('code'));

    if (accountFetching) return;

    if (accountError || !accountData?.person_by_pk) {
      setError('code', {
        type: 'manual',
        message: 'Unable to check your account. Please try again.',
      });
      return;
    }

    if (!isAccountComplete) {
      setJoinIntent({ kind, code: code, memberType });
      markJoinIntentForResume();
      router.replace('/welcome');
      return;
    }

    setIsAccepting(true);
    const { data, error } = await acceptJoin({
      kind,
      code: code,
      memberType,
    });
    setIsAccepting(false);

    const result = data?.accept_invite_entry[0];
    const entityId = result?.entity_id;
    if (error || !entityId) {
      if (isJoinTerminalFailure(entityId, error)) {
        discardJoinIntent({ kind, code: code, memberType });
      }
      setError('code', {
        type: 'manual',
        message:
          kind === 'team' && preview.status === 'locked'
            ? `${preview.title} is not accepting new members.`
            : `Could not join ${kind}. Please request a new code and try again.`,
      });
      retryPreview({ requestPolicy: 'network-only' });
      return;
    }

    clearJoinIntent();
    if (kind === 'organization') gotoOrganizationDetail(entityId);
    else gotoTeamDetail(entityId);
  };

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: `Join ${kind === 'team' ? 'a Team' : 'an Organization'}`,
          headerLeft,
        }}
      />
      <WelcomeModalStepFrame
        title={`Join ${kind === 'team' ? 'a team' : 'an organization'}`}
        subtitle={`Enter the invite code you received.`}
        footer={
          <WelcomeActions>
            <WelcomePrimaryAction
              label={actionLabel}
              loading={isBusy}
              loadingLabel={isAccepting ? 'Joining…' : 'Checking…'}
              disabled={!isValid || isBusy || joinUnavailable}
              onPress={() => {
                if (previewError) {
                  retryPreview({ requestPolicy: 'network-only' });
                  return;
                }
                void continueToAcceptance();
              }}
            />
          </WelcomeActions>
        }
      >
        <WelcomeCard>
          <Controller
            control={control}
            name="code"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Box space="md" center>
                <CodeInput
                  isDisabled={previewFetching || isAccepting}
                  value={value}
                  onChange={(nextValue) => {
                    clearErrors('code');
                    onChange(nextValue);
                  }}
                />
                <Box space="sm" align="center">
                  <Text center>Join as</Text>
                  <MemberTypeSelector
                    labelForm="singular"
                    value={memberType}
                    onValueChange={setMemberType}
                  />
                </Box>
                {joinUnavailable ? (
                  <Text center variant="error" size="sm">
                    Invalid invite code. Please try again.
                  </Text>
                ) : null}
                {previewError ? (
                  <Text center variant="error" size="sm">
                    Unable to verify the invite code. Please try again.
                  </Text>
                ) : null}
                {error && error.type !== 'length' ? (
                  <Text center variant="error" size="sm">
                    {error.message}
                  </Text>
                ) : null}
              </Box>
            )}
          />
        </WelcomeCard>
      </WelcomeModalStepFrame>
    </FormProvider>
  );
}
